import React, { useEffect, useMemo, useState } from 'react'
import type { SlotInfo, View } from 'react-big-calendar'
import { Calendar as BigCalendar, momentLocalizer } from 'react-big-calendar'
import moment from 'moment'
import 'react-big-calendar/lib/css/react-big-calendar.css'
import type { MuiTheme as Theme } from '@paintscout/ui'
import {
  Card,
  makeStyles,
  useClientOptions,
  useDialogs,
  Hidden,
  useBreakpoint,
  useIsMobile,
  EventsMap,
  EventPreviewPopper,
  useUser,
  useUsers
} from '@paintscout/ui'
import type { CalendarEvent } from 'paintscout'
import { getTimezone, getVisibleCalendarEvents, groupEventsByEstimator, uuid } from '@paintscout/util/builder'
import { useCancelEventFlow } from 'src/hooks'
import { useLocation } from 'react-router-dom'
import { setUrlQuery } from 'shared/util/routing'
import { EventDialog, ViewEventsDialog } from '../../dialogs'
import { ShowWeekendsSwitch } from '../CalendarToolbar'
import { useCalendar } from './CalendarContext'
import CalendarShim from './CalendarShim'
import { DayEvent } from './day'
import { MobileMonthView, MonthColumnHeader, MonthDateHeader, MonthEvent, ShowMore } from './month'
import { WeekColumnHeader, WeekEvent } from './week'

const NONE_IMPORTANT = 'none !important'
const ZERO_IMPORTANT = '0 !important'
import { getEventTimes, getMapTitle, handleCalendarNavigate } from './calendarUtils'
import SalesCalendarToolbar from './toolbar/SalesCalendarToolbar'

interface CalendarStyleArgs {
  view: View
  tab: string
  hideWeekends: boolean
  hideAllDayCell: boolean
}

const useStyles = makeStyles<Theme, CalendarStyleArgs>((theme) => ({
  root: {
    height: 'max(calc(100dvh - 300px), 800px)',
    maxHeight: ({ view, tab }) => (view === 'day' || tab === 'map' ? '825px' : '1055px'),
    borderRadius: theme.borderRadius.md,
    '& .rbc-month-view, & .rbc-time-view': {
      padding: theme.spacing(2),
      border: 'none',
      [theme.breakpoints.down('xs')]: {
        padding: theme.spacing(1)
      }
    },
    [theme.breakpoints.down('xs')]: {
      height: ({ view, tab }) => (view === 'month' && tab !== 'map' ? 'auto' : '600px'),
      paddingBottom: ({ view, tab }) => (tab === 'map' ? 0 : view === 'week' ? theme.spacing(4) : theme.spacing(3))
    },
    '& .rbc-allday-cell': {
      display: ({ hideAllDayCell }) => (hideAllDayCell ? 'none' : 'block'),
      marginBottom: ({ view }) => (view === 'day' ? theme.spacing(0) : theme.spacing(-2)),
      [theme.breakpoints.down('xs')]: {
        marginBottom: ({ view }) => (view === 'day' ? theme.spacing(0) : '-8px !important')
      },
      '& .rbc-day-bg': {
        backgroundColor: 'transparent !important',
        outline: NONE_IMPORTANT
      },
      '& .rbc-row-segment': {
        padding: 0,
        paddingBottom: theme.spacing(0.5)
      },
      '& .rbc-row-content': {
        '& .rbc-row:empty': {
          height: 0,
          minHeight: 0
        }
      }
    },
    '& .rbc-allday-cell .rbc-row-bg .rbc-day-bg': {
      border: NONE_IMPORTANT
    },
    '& .rbc-time-header': {
      border: 'none',
      '& > *': {
        border: 'none'
      }
    },

    '& .rbc-label.rbc-time-header-gutter': {
      marginRight: theme.spacing(1)
    },
    '& .rbc-time-header.rbc-overflowing': {
      borderRight: 'none'
    },
    '& .rbc-header': {
      border: NONE_IMPORTANT,
      height: 'fit-content'
    },
    '& .rbc-today': {
      backgroundColor: theme.palette.common.white
    },
    '& .rbc-button-link': {
      width: '100%'
    },
    '& .rbc-day-bg': {
      border: '2px solid transparent !important',
      borderRadius: theme.borderRadius.md,
      backgroundColor: theme.paintscout.palette?.brand?.Neutral?.P98,
      margin: theme.spacing(0.5),
      cursor: 'pointer',
      // Only apply hover styles on devices that support hover
      '@media (hover: hover) and (pointer: fine)': {
        '&:hover': {
          border: `2px dashed ${theme.paintscout.palette.brand.Neutral.P60} !important`
        }
      }
    },
    '& .rbc-day-bg.rbc-today': {
      outline: `1px solid ${theme.paintscout.palette.primary} !important`,
      '@media (hover: hover) and (pointer: fine)': {
        '&:hover': {
          outline: NONE_IMPORTANT
        }
      }
    },
    '& .rbc-day-bg.disabled-day': {
      pointerEvents: 'none'
    },
    '& .rbc-month-row + .rbc-month-row': {
      borderTop: 'none'
    },
    '& .rbc-row-content': {
      pointerEvents: 'none',
      margin: theme.spacing(0.5)
    },
    '& .rbc-events-container': {
      margin: '0px 4px 0px 4px !important',
      top: '-3px'
    },
    '& .rbc-timeslot-group': {
      border: 'none',
      minHeight: '70px',
      maxHeight: '70px'
    },
    '& .rbc-time-slot': {
      border: '2px solid transparent !important',
      margin: theme.spacing(0.5),
      backgroundColor: theme.paintscout.palette?.brand?.Neutral?.P98,
      cursor: 'pointer',
      zIndex: 1000,
      borderRadius: theme.borderRadius.md,
      [theme.breakpoints.up('md')]: {
        '@media (hover: hover) and (pointer: fine)': {
          '&:hover': {
            border: `2px dashed ${theme.paintscout.palette.brand.Neutral.P60} !important`
          }
        }
      }
    },
    // Top half covered — draw dashed border only around the bottom half on hover
    '& .rbc-time-slot.rbc-time-slot-covered-top': {
      marginTop: '41px !important'
    },
    // Bottom half covered — draw dashed border only around the top half on hover
    '& .rbc-time-slot.rbc-time-slot-covered-bottom': {
      maxHeight: '28px !important'
    },
    '& .rbc-time-content': {
      border: 'none'
    },
    '& .rbc-time-content > * + * > *': {
      borderLeft: 'none'
    },
    '& .rbc-day-slot.rbc-time-column': {
      '& .rbc-timeslot-group:first-child': {
        '& .rbc-time-slot': {
          marginTop: ZERO_IMPORTANT
        }
      }
    },
    '& .rbc-row-segment': {
      padding: theme.spacing(0, 2, 0.5, 2)
    },
    '& .rbc-time-gutter': {
      pointerEvents: 'none',
      borderRadius: theme.borderRadius.md,
      color: theme.paintscout.palette.brand.Neutral.P50,
      margin: theme.spacing(0, 1, 0, 0),
      padding: theme.spacing(0, 1, 0, 3),
      '& .rbc-timeslot-group': {
        fontWeight: 500,
        margin: ZERO_IMPORTANT,
        '& .rbc-time-slot': {
          margin: ZERO_IMPORTANT,
          backgroundColor: 'transparent !important',
          '& .rbc-timeslot-period': {
            fontSize: theme.typography.pxToRem(12)
          }
        }
      },
      [theme.breakpoints.down('xs')]: {
        padding: 0,
        marginRight: theme.spacing(0.5)
      }
    },
    '& .rbc-current-time-indicator': {
      display: 'none'
    },
    '& .rbc-event': {
      padding: 0,
      backgroundColor: 'transparent',
      border: NONE_IMPORTANT,
      zIndex: 1001,
      height: '20px',
      pointerEvents: 'auto',
      transform: ({ view }) => `translateY(${view === 'day' ? 2 : view === 'week' ? 3 : -4}px)`,
      '& .rbc-event-label': {
        display: 'none '
      },
      '&:focus': {
        outline: 'none'
      },
      '&:has(.rbc-event-unavailable)': {
        width: '100% !important',
        overflow: 'visible'
      },
      '@media (hover: hover) and (pointer: fine)': {
        '&:hover:not(:has(.rbc-event-unavailable)):not(:has(.rbc-event-imported))': {
          boxShadow: ({ view }) => theme.shadows[view === 'month' ? 1 : 5]
        }
      }
    },

    '& .rbc-row-bg': {
      '& .rbc-day-bg:first-child': {
        display: ({ hideWeekends, view }) => hideWeekends && view !== 'week' && 'none'
      },
      '& .rbc-day-bg:last-child': {
        display: ({ hideWeekends, view }) => hideWeekends && view !== 'week' && 'none'
      }
    },
    '& .rbc-row': {
      '& .rbc-date-cell:first-child': {
        display: ({ hideWeekends }) => hideWeekends && 'none'
      },
      '& .rbc-date-cell:last-child': {
        display: ({ hideWeekends }) => hideWeekends && 'none'
      }
    },
    '& .rbc-date-cell': {
      pointerEvents: 'none'
    },

    '& .rbc-row.rbc-month-header': {
      '& .rbc-header:first-child': {
        display: ({ hideWeekends }) => hideWeekends && 'none'
      },
      '& .rbc-header:last-child': {
        display: ({ hideWeekends }) => hideWeekends && 'none'
      }
    },
    '& .rbc-event-allday': {
      backgroundColor: 'transparent',
      height: 'fit-content'
    },
    '& .rbc-slot-selection': {
      display: 'none'
    }
  },
  weekendsSwitch: {
    color: theme.paintscout.palette.brand.Neutral.P50,
    display: 'flex',
    height: '21px',
    '& label': {
      marginLeft: theme.spacing(1)
    }
  }
}))

type MapGranularity = 'day' | 'week' | 'month'

let latestMapGranularity: MapGranularity = 'day'

export default function Calendar() {
  const {
    date,
    setDate,
    tab,
    loading,
    view,
    setView,
    hideWeekends,
    events,
    allEvents,
    selectedUsers,
    addEvent,
    updateEvent,
    deleteEvent
  } = useCalendar()
  const location = useLocation()
  const { options } = useClientOptions()
  const timezone = getTimezone({ options })
  const localizer = momentLocalizer(moment)
  const mobile = useIsMobile({ xs: true })
  const isSmallScreen = useBreakpoint((breakpoints) => breakpoints.down('md'))
  const { user, isSales } = useUser()
  const { users } = useUsers()
  const { openDialog, dismissAllDialogs, stack: dialogStack } = useDialogs()

  const isMultiUsersMonthView = view === 'month' && users && users.length > 1 && selectedUsers.length > 1 && !isSales
  const isMonthViewOnSmallScreen = view === 'month' && isSmallScreen

  const mapGranularity: MapGranularity =
    view === 'day' || view === 'week' || view === 'month'
      ? (view as MapGranularity)
      : view === 'work_week'
        ? 'week'
        : 'month'
  latestMapGranularity = mapGranularity

  const calendarView = tab === 'map' ? 'map' : view

  const eventsToDisplay = isMultiUsersMonthView || isMonthViewOnSmallScreen ? groupEventsByEstimator(events) : events

  const visibleEvents = getVisibleCalendarEvents({
    events: eventsToDisplay,
    view: view as 'day' | 'week' | 'month',
    date,
    hideWeekends: hideWeekends && tab !== 'map'
  })

  const unavailableEvents = useMemo(
    () => eventsToDisplay.filter((e) => e.eventType === '_unavailable_'),
    [eventsToDisplay]
  )
  const classes = useStyles({
    view,
    tab,
    hideWeekends: hideWeekends && view === 'week',
    hideAllDayCell: !(visibleEvents || []).some((event) => event.allDay)
  })

  const handleSetView = (newView: View | 'map') => {
    if (newView === 'map') {
      return
    }

    setView(newView)
  }

  const getDayEvents = (date: Date) => {
    return events.filter((event) => {
      const eventStart = moment(event.start)
      const eventEnd = moment(event.end)
      const targetDate = moment(date)

      return (
        // The target date is the same or after the event start (so the event has started)
        targetDate.isSameOrAfter(eventStart, 'day') &&
        // The target date is before the event end (so the event is ongoing)
        (targetDate.isBefore(eventEnd, 'day') ||
          // Or the target date is the same as the event end,
          (targetDate.isSame(eventEnd, 'day') &&
            // but only include if the event does NOT end exactly at midnight (00:00:00.000)
            !(
              eventEnd.hour() === 0 &&
              eventEnd.minute() === 0 &&
              eventEnd.second() === 0 &&
              eventEnd.millisecond() === 0
            )))
      )
    })
  }

  const buildNewEvent = (start?: Date, end?: Date): Partial<CalendarEvent> => {
    const estimator =
      selectedUsers?.length === 1
        ? {
            id: selectedUsers[0].user_id,
            name: selectedUsers[0].name,
            color: selectedUsers[0].user_metadata?.color,
            email: selectedUsers[0].email
          }
        : {
            id: user?.user_id,
            name: user?.name,
            color: user?.user_metadata?.color,
            email: user?.email
          }
    return {
      id: uuid(),
      start,
      end,
      estimator
    }
  }

  const handleSelectSlot = (slotInfo: SlotInfo) => {
    if (slotInfo.slots.length > 2 || dialogStack.length > 0) {
      return
    }

    if (view === 'month') {
      onMonthSlotClicked(slotInfo.start || slotInfo?.slots?.[0])
      return
    }

    const { start, end } = getEventTimes(slotInfo.start || slotInfo?.slots?.[0], view, timezone)

    if (moment(start).isBefore(moment(), 'minute')) {
      return
    }

    onSlotClicked(buildNewEvent(start, end))
  }

  const handleDirectSlotClick = (date: Date, coverage?: 'top' | 'bottom') => {
    if (dialogStack.length > 0) {
      return
    }
    if (view === 'month') {
      onMonthSlotClicked(date)
      return
    }
    const { start, end } = getEventTimes(date, view, timezone)
    let finalStart = start
    let finalEnd = end
    if (coverage === 'bottom') {
      // Bottom half covered — clickable area is :00 to :30
      finalEnd = moment(start).add(30, 'minutes').toDate()
    } else if (coverage === 'top') {
      // Top half covered — clickable area is :30 to end of hour
      finalStart = moment(start).add(30, 'minutes').toDate()
    }
    if (moment(finalStart).isBefore(moment(), 'minute')) {
      return
    }

    onSlotClicked(buildNewEvent(finalStart, finalEnd))
  }

  const onSlotClicked = (newEvent: Partial<CalendarEvent>, date?: Date) => {
    openDialog(EventDialog, {
      event: newEvent,
      date,
      isNew: true,
      selectedUsers: view === 'month' ? selectedUsers : undefined,
      onConfirm: (event: CalendarEvent, eventTemplate: string) => {
        addEvent(event, eventTemplate)
        dismissAllDialogs()
      }
    })
  }

  const onMonthSlotClicked = (date: Date) => {
    const dayEvents = getDayEvents(date)

    const isInPast = moment(date).isBefore(moment(), 'day')

    if (dayEvents.length === 0 && isInPast) {
      return
    }

    if (dayEvents.length === 0) {
      if (moment(date).isSame(moment(), 'day')) {
        const { start, end } = getEventTimes(date, view, timezone)
        onSlotClicked(buildNewEvent(start, end), date)
      } else {
        onSlotClicked(buildNewEvent(), date)
      }

      return
    }

    const hasMultipleEstimators =
      dayEvents.length > 1 && !dayEvents.every((event) => event.estimator.id === dayEvents[0].estimator.id)

    openDialog(ViewEventsDialog, {
      date,
      events: hasMultipleEstimators ? groupEventsByEstimator(dayEvents) : dayEvents,
      updateEvent,
      deleteEvent,
      addEvent,
      groupByEstimator: hasMultipleEstimators,
      selectedUsers
    })
  }

  // Check url for an id that we want to open in dialog
  useEffect(() => {
    const eventIdMatch = location.search.match(/&([a-z0-9-]+)\/?$/)
    if (allEvents.length && eventIdMatch && eventIdMatch[1]) {
      setUrlQuery({
        location,
        replaceParams: true,
        params: {
          tab: 'calendar',
          search: null,
          filter: null
        }
      })
      const eventId = eventIdMatch[1]
      const event = allEvents.find((e) => e.id === eventId)
      setDate(event.start)
      openDialog(EventDialog, {
        event,
        onDelete: deleteEvent,
        onConfirm: (event: CalendarEvent, eventTemplate: string) => {
          updateEvent(event, eventTemplate)
          dismissAllDialogs()
        }
      })
    }
  }, [allEvents])
  return (
    <Card className={classes.root}>
      <BigCalendar<CalendarEvent>
        date={date}
        view={calendarView as unknown as View}
        onView={handleSetView}
        events={eventsToDisplay}
        localizer={localizer}
        timeslots={1}
        step={60}
        formats={{
          dateFormat: 'D',
          timeGutterFormat: 'ha',
          dayFormat: 'MMMM D, YYYY'
        }}
        scrollToTime={moment().toDate()}
        components={{
          toolbar: SalesCalendarToolbar,
          month: { header: MonthColumnHeader, dateHeader: MonthDateHeader, event: MonthEvent },
          week: { header: WeekColumnHeader, event: WeekEvent },
          day: { event: DayEvent },
          showMore: ShowMore
        }}
        workdaysOnly={hideWeekends}
        dayLayoutAlgorithm={'no-overlap'}
        selectable={tab !== 'map' && view === 'month'}
        views={
          {
            day: loading ? CalendarShim : true,
            week: loading ? CalendarShim : true,
            month: loading ? CalendarShim : mobile ? MobileMonthView : true,
            map: loading ? CalendarShim : MapView
          } as Record<string, boolean | React.ComponentType<any>>
        }
        longPressThreshold={5}
        onSelectSlot={handleSelectSlot}
        onSelecting={() => false}
        slotPropGetter={(date) => {
          const slotStart = moment(date)
          const slotMid = moment(date).add(30, 'minutes')
          const slotEnd = moment(date).add(60, 'minutes')
          const topCovered = unavailableEvents.some(
            (e) => slotStart.isBefore(moment(e.end)) && slotMid.isAfter(moment(e.start))
          )
          const bottomCovered = unavailableEvents.some(
            (e) => slotMid.isBefore(moment(e.end)) && slotEnd.isAfter(moment(e.start))
          )
          const slotClass = topCovered
            ? 'rbc-time-slot-covered-top'
            : bottomCovered
              ? 'rbc-time-slot-covered-bottom'
              : undefined
          const coverage = topCovered ? 'top' : bottomCovered ? 'bottom' : undefined
          return {
            onClick: () => handleDirectSlotClick(date, coverage),
            style: moment(date).isBefore(moment()) ? { pointerEvents: 'none' } : {},
            className: slotClass
          }
        }}
        dayPropGetter={(date) => {
          if (view === 'month' && moment(date).isBefore(moment(), 'day') && getDayEvents(date).length === 0) {
            return {
              className: 'disabled-day'
            }
          }
          return {}
        }}
        onDrillDown={(date: Date, view: View) => {
          setDate(date)
          handleSetView(view)
        }}
      />
      <Hidden smUp>
        {view === 'week' && tab === 'calendar' && !loading && (
          <div className={classes.weekendsSwitch}>
            <ShowWeekendsSwitch />
          </div>
        )}
      </Hidden>
    </Card>
  )
}

function EventsMapView(props: any) {
  const { openDialog, dismissDialog } = useDialogs()
  const { events, updateEvent, deleteEvent, selectedUsers, view, fetchingIntegrationEvents } = useCalendar()
  const { users } = useUsers()
  const mobile = useIsMobile()
  const cancelEventFlow = useCancelEventFlow()
  const [selectedEvent, setSelectedEvent] = useState<CalendarEvent | null>(null)
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)

  const handleMarkerClick = (event: CalendarEvent, e?: React.MouseEvent<HTMLElement>) => {
    if (mobile) {
      setSelectedEvent(event)
      setAnchorEl((e?.currentTarget as HTMLElement) || document.body)
    } else {
      openDialog(EventDialog, {
        event,
        onDelete: deleteEvent,
        onConfirm: (event: CalendarEvent, eventTemplate: string) => {
          updateEvent(event, eventTemplate)
          dismissDialog()
        }
      })
    }
  }

  const handleCloseDrawer = () => {
    setAnchorEl(null)
    setTimeout(() => {
      setSelectedEvent(null)
    }, 300)
  }

  const handleEditEvent = (event: CalendarEvent) => {
    setAnchorEl(null)
    openDialog(EventDialog, {
      event,
      onDelete: deleteEvent,
      onConfirm: (updatedEvent: CalendarEvent, eventTemplate: string) => {
        updateEvent(updatedEvent, eventTemplate)
        dismissDialog()
      }
    })
  }

  const handleCancelEvent = (event: CalendarEvent) => {
    setAnchorEl(null)
    cancelEventFlow(event, deleteEvent)
  }

  const selectedEstimators = selectedUsers.map((user) => user.user_id)
  const date = props.date
  const timeSpan = view === 'month' ? 'month' : view === 'week' ? 'week' : 'day'
  const filteredEvents = events.filter((event) => {
    const eventDate = moment(event.start).isSame(date, timeSpan)
    const isInSelectedEstimators = selectedEstimators.includes(event.estimator?.id)
    return eventDate && isInSelectedEstimators
  })

  const isDayView = view === 'day'

  return (
    <>
      <EventsMap
        {...props}
        dayView={isDayView}
        events={filteredEvents}
        onMarkerClick={handleMarkerClick}
        loading={fetchingIntegrationEvents}
      />
      {mobile && selectedEvent && (
        <EventPreviewPopper
          event={selectedEvent}
          anchorEl={anchorEl}
          setAnchorEl={setAnchorEl}
          users={users}
          onClose={handleCloseDrawer}
          onEditEvent={handleEditEvent}
          onCancelEvent={handleCancelEvent}
        />
      )}
    </>
  )
}

const MapView = (props) => <EventsMapView {...props} />
MapView.title = (date: Date) => getMapTitle(date, latestMapGranularity)
MapView.navigate = (date: Date, action: string) => handleCalendarNavigate(date, action, latestMapGranularity)
